import curry from 'lodash/fp/curry';
import pipe from 'lodash/fp/pipe';

// VALUE UTILS

/**
 * @func IDENTITY
 * @desc a function that takes a value and returns it
 *
 * @param {*} value
 * @return {*} value
 *
 * The need for this function isn't immediately obvious, but it does come up
 * more often that one would assume. Imagine a function that takes another
 * function as its argument, but all you  have to pass to it is a static value.
 *
 * @example
 *  const updateConfigs = (originalConfig) => converge(
 *    merge,
 *    [
 *      IDENTITY // -> our original value
 *      computeNewStageModel,
 *      computeNewComponentsList,
 *      computeNewScenarios,
 *    ]
 *  )(originalConfig)
 *
 * Or a filter operation where you want to levarage the language's concept of
 * "truthyness" to determine "emty" or invalid values.
 *
 * @example
 *  [undefined, 1, true, 'string', null, {}].filter(IDENTITY) // -> [1, true, 'string', {}]
 *
 * Or you need to take a nested array and flatten its contents using `Array.prototype.flatMap`
 * @example
 *  [ ['a', 'b', 'c'], [1, 2, 3 ], [true, false] ].flatMap(IDENTITY) // -> ['a', 'b', 'c', 1, 2, 3, true, false]
  * */
export const IDENTITY = (value) => value;

/**
 * @func not
 * @desc negate a given value
 * @param {*} val
 * @return {Boolean}
 *
 * (val: any) => Boolean
 *
 * @example
 *  const hasNoRef = compose(not, prop('ref'), prop('stageElements'))
 *  [{ ref: 'FSHWfxl6rg0ofZbJzv3FZ' }, { ref: 'FSHWfxl6rg0ofZbJzv3FZ'}, { foo: 'bar' }].filter(hasNoRef).forEach(assignRef)
  * */
export const not = (val) => Boolean(!val);

// ARITHMETIC UTILS

/**
 * @func add
 * @desc addition
 * @param {Number} x
 * @param {Number} y
 * @return {Number}
 *
 * (x: Number) => (y: Number) => Number
 *
 * @example
 *  const addTen = add(10)
 *  addTen(3) // -> 13
 *  addTen(100) // -> 110
  * */
export const add = (x) => (y) => Number(x) + Number(y);

/**
 * @func inc
 * @param {Number} n
 * @return {Number}
 *
 * (n: Number) => Number
  * */
export const inc = add(1);

/**
 * @func dec
 * @param {Number} n
 * @return {Number}
 *
 * (n: Number) => Number
  * */
const dec = add(-1);

// LIST UTILS

/**
 * @func head
 * @desc grab the first item from a list
 * @param {Array<T>} array
 * @return {T}
 *
 * (array: T[]) => T
 *
 * @example
 *  const nextAction = head(actionQueue)
 * @example
 *  const sectionIndex = head([1, 0, 0, 1]) // -> 1
  * */
export const head = (array) => array.slice().shift();

/**
 * @func last
 * @desc grab the last item from a list
 * @param {Array<T>} array
 * @return {T}
 *
 * (array: T[]) => T
 *
 * @example
 *  const latestErr = last(logs)
 * @example
 *  const elementIndex = last([1, 0, 0, 1]) // -> 1
  * */
export const last = (array) => array.slice().pop();

/**
 * @func map
 * @desc pointfree map
 * @param {Function} fn - mapping function
 * @param {Array<T>} array - list
 * @return {Array<T1>}
 *
 * (fn: (t: T) => T1, array: T[]) => T1[]
 *
 * @example
 *  const serializeAll = map(serialize)
 *  serializeAll(elementList)
  * */
export const map = curry((fn, array) => array.map(fn));

/**
 * @func filter
 * @desc pointfree filter function
 * @param {Function(T): Boolean} pred
 * @param {Array<T>} array
 * @return {Array<T>}
 *
 * ((x: any) => Boolean) => (array: any[]) => Boolean
 *
 * @example
 *  const hasRefs = filter(x => !!x.ref)
 *  const validElements = hasRefs(longElementList)
  * */
export const filter = (pred) => (array) => array.filter(pred);

/**
 * @func zip
 * @desc create a list of pairs from two lists
 * @param {Array<T>} array
 * @param {Array<T1>} array2
 * @return {Array<T,T1>}
 *
 * (array: T[]) => (array2: T1[]) => [T, T1][]
 *
 * @example
 *  const listA = ['sections', 'rows', 'columns', 'elements']
 *  const listB = [0, 1, 0, 1]
 *  zip(listA)(listB) // -> [['sections', 0], ['rows', 1], ['columns', 0], ['elements', 1]]
 * @example
 *  const listA = ['sections', 'rows', 'columns', 'elements']
 *  const listB = [0]
 *  zip(listA)(listB) // -> [['sections', 0]]
  * */
export const zip = (array) => (array2) => array.map((value, index) => [value, array2[index]]);

/**
 * @func flatten
 * @desc "flatten" nested lists
 * @param {Array<Array<T>>} array
 * @return {Array<T>}
 *
 * (array: T[][]) => T[]
 *
 * @example flatten([['sections', 0], ['rows', 1], ['columns', 0], ['elements', 1]]) // -> ['sections', 0, 'rows', 1, 'columns', 0, 'elements' 1]
  * */
export const flatten = (array) => array.flatMap(IDENTITY);

/**
 * @func append
 * @desc add an item to the end of a list
 * @param {T} value
 * @param {Array<T>} array
 * @return {Array<T>}
 *
 * (x: T) => (array: T[]) => T[]
 *
 * @example
 *  const addLatest = append(store.get())
 *  addLatest(log)
 *
 * @example
 *  const exclaim = append('!!')
 *  exclaim('Hello, World') // -> 'Hello, World!!'
  * */
export const append = (value) => (array = []) => array.concat([value]);

/**
 * @func prepend
 * @param {T} value
 * @param {Array<T>} array
 * @return {Array<T>}
 *
 * (value: T) => (array: T[]) => T[]
 *
 * @example
 *  prepend(stage.get(), actionStack) // -> ['LATEST_ACTION' ...prevActions]
 *
 * @example
 *  const greet = prepend('Good, ')
 *  greet('Morning') // -> 'Good, Morning'
  * */
export const prepend = (value) => (array) => [value].concat(array);

/**
 * @func lastIndex
 * @desc compute the last index of a list
 * @param {Array<*>}
 * @return {Number}
 *
 * (array: any[]) => Number
 *
 * @example
 *  const logs = ['bad', 'terrible', 'no good', 'horrible']
 *  const actions = ['POST', 'GET', 'PUT', 'DELETE', 'POST', 'GET', 'GET', 'POST']
 *  const findLatestError = actions[lastIndex(logs)] // -> 'DELETE'
  * */
const lastIndex = (array) => Math.floor(dec(array.length));

/**
 * @func dropLast
 * @desc create a new array with all values from another array except the last
 * @param {Array<T>} array
 * @return {Array<T>}
 *
 * (array: T[]) => T[]
 *
 * @example
 *  const eats = ['breakfast', 'lunch', 'dinner', 'snack']
 *  const healthyDiet = dropLast(eats).concat(['water']) // -> ['breakfast', 'lunch', 'dinner', 'water']
  * */
export const dropLast = (array) => array.slice(0, lastIndex(array));

/**
 * @func updateAtIndex
 * @desc create a copy of a list, applying a function to the value at a given index
 * @param {Number} i - target index to update
 * @param {Function(T): T} fn
 * @param {Array<T>}
 * @return {Array<T>}
 *
 * (i: Number) => (fn: (T) => T) => (T[]) => T[]
 *
 * @example updateAtIndex(1)(toUpperCase)(['foo', 'bar', 'baz']) // -> ['foo', 'BAR', 'baz']
  * */
export const updateAtIndex = (index = 0) => (fn = IDENTITY) => (array = []) =>
  array.length <= index
    ? updateAtIndex(lastIndex(array))(fn)(array)
    : array.slice(0, index).concat([fn(array[index])]).concat(array.slice(index + 1));

/**
 * @func updateLast
 * @param {Function(T): T} fn
 * @param {Array<T>}
 * @return {Array<T>}
 *
 * (fn: (T) => T) => (T[]) => T[]
 *
 * @example updateLast(toUpperCase)(['foo', 'bar', 'baz']) // -> ['foo', 'bar', 'BAZ']
  * */
export const updateLast = updateAtIndex(Infinity);

/**
 * @func insertAtIndex
 * @param {Number} i - target index to update
 * @param {T} x
 * @param {Array<T>} array
 * @return {Array<T>}
 *
 * (i: Number) => (x: T) => (array: T[]) => T[]
 *
 * @example insertAtIndex(1)('tiki')(['foo', 'bar', 'baz']) // -> ['foo', 'tiki', 'bar', 'baz']
  * */
export const insertAtIndex = (index = 0) => (value) => (array = []) =>
  (array.length < index)
    ? array.concat([value])
    : array.slice(0, index).concat([value]).concat(array.slice(index));

// OBJECT UTILS

/**
 * @func merge
 * @desc Concatenate data objects together in left-to-right order
 * @param {...Object} data
 * @return {Object}
 *
 * (...data: Object[]) => Object
 * */
export const merge = (...data) => Object.assign({}, ...data);

/**
 * @func prop
 * @desc get the value of a given property from an object
 * @param {String} key
 * @param {object} object
 * @return {*}
 *
 * @example prop('foo')({ foo: 'bar' }) // -> 'bar'
 * @example
 *  const getId = prop('id')
 *  getId({ id: '1234' }) // -> '1234'
 *  getId({ id: 'avenue-five' }) // -> 'avenue-five'
 *
 * @example
 *  const getFirst = prop('0')
 *  getFirst(['a', 'b', 'c']) // -> a
 *  getFirst([1, 2, 3, 4, 5]) // -> 1
  * */
export const prop = (key) => (object) => object[key];

/**
 * @func assoc
 * @desc assign a key/value pair to an object
 * @param {String} key
 * @param {*} value
 * @param {Object} data
 * @return {Object}
 *
 * @example
 *  const setId = assoc('id', makeId())
 *  setId({ foo: 'bar' }) // -> { foo: bar, id: 'SJymbl5EsxDIqAVNUFSFK' }
 * @example
 *  const assignRef = (data) => assoc('ref', uuid(), data)
 *  [{ type: 'stage-element-text'  }, { type: 'stage-element-image' }, { type: 'state-element-placeholder' }].map(assignRef)
 *  // -> [{ type: 'stage-element-text' , ref: 'TCfNwGRptSQqngLveIyVC' }, { type: 'stage-element-image', ref: 'WItn5CIKEWO1UNHO836jJ' }, { type: 'state-element-placeholder', ref: '9DwalJyJtVL70vBhift2L' }]
  * */
const assoc = curry((key, value, data) => ({ ...data, [key]: value }));

/**
 * @func fromProp
 * @desc generate a new object containing only a specific property from an existing object
 * @param {String} key - key of propery to copy
 * @param {Object} object - source object containing propert
 * @return {Object}
  * */
export const fromProp = (key) => (object) => assoc(key, object[key], {});

/**
 * @func toPairs
 * @desc transform an object to a list of key/value pairs
 * @param {Object} obj
 * @return {Array<[String, *]>}
 *
 * @example
 *  toPairs({ foo: 'bar', bool: true }) // -> [ ['foo', 'bar'], ['bool', true] ]
  * */
const toPairs = (obj) => Object.entries(obj);

/**
 * @func fromPairs
 * @desc transform a list of key/value pairs into an object
 * @param {Array<[String, *]>} entries
 * @return {Object}
 *
 * @example
 *  fromPairs([['foo', 'bar'], ['bool', true]]) // -> { foo: 'bar', bool: true }
  * */
const fromPairs = (entries) => Object.fromEntries(entries);

export const filterProps = (pred) => pipe(
  toPairs,
  filter(([key]) => pred(key)),
  fromPairs
);

// CONTROL FLOW UTILS

/**
 *
 * @func
 * @curried
 * @desc
 *  Execute a given function on the returned values from a list of functions
 *  Useful for function composition that requires branching
 *
 * @param {Function} after - function to execute on list of return values
 * @param {Array<Function>} fns - functions to map on a given value
 * @return {*}
 *
 * @example converge(concat, [toUpper, toLower], "Yodel") // -> "YODELyodel"
 * @example converge(divide, [sum, length], [1, 2, 3, 4, 5, 6, 7]) // -> calculate the average 4
 * */
export const converge = curry((finalFn, arrayOfFns) =>
  curry((...args) =>
    finalFn(...map((fn) => fn(...args), arrayOfFns))
  )
);

/**
 * @func tap
 * @desc apply a function to a value within a composition/pipeline and return the original value
 *
 * @param {function: void} fn
 * @param {T} value
 * @return {T}
 *
 * @example
 *  const numToWord = (num) => {
 *    switch (num) {
 *      case 1: return 'one'
 *      case 2: return 'two'
 *      case 3: return 'three'
 *      case 4: return 'four'
 *      case 5: return 'five'
 *      default: return 'zero'
 *    }
 *  }
 *  const repeat = (times) => (string) => string + string + string
 *  const exclaim = (wordy) => word + '!!!'
 *
 *  let cache;
 *  const loudNumber = pipe(
 *    numToWord,
 *    tap(console.log) // logs -> 'three'
 *    repeat(3),
 *    tap((num) => cache = num) // perform some side-effect
 *    toUpperCase,
 *    tap(console.log) // logs -> 'THREETHREETHREE'
 *    exclaim
 *  )(3)
 *
 *  console.log(loudNumber) // logs -> 'THREETHREETHREE!!!'
 *  console.log(cache) // logs -> 'threethreethree'
  * */
const tap = (fn) => (value) => { fn(value); return value; };
export const performSideEffectWithValueAndReturnOriginal = tap;

// STAGE MODEL UTILS

/**
 * @func incrementTargetIndex
 * @param {Number[]} stagePath - current selected path
 * @return {Number[]} target path
 *
 * (stagePath: Number[]) => Number[]
  * */
export const incrementTargetIndex = (stagePath) => !stagePath.length
  ? stagePath
  : updateLast(inc)(stagePath);

/**
 * @func formatStagePath
 * @param {Array<[String, Number]>} keyPath
 * @return {Array<String|Number>}
 * (keyPath: [String, Number][]) => Array<String|Number>
  * */
export const formatStagePath = pipe(
  flatten,
  prepend('stage'),
);

/** 
   * Find the parent object that contains target key, and transform the parent object
   * @param {string} childKey
   * @param {function} transform
   * @return {(value: object) => void)}
   * */
export const findAndReplace = (childKey, transform) => function (obj) {
  const value = JSON.stringify(obj, function (_, value) {
    if (value instanceof Object && childKey in value && transform) {
      return transform(value);
    } else {
      return value;
    }
  });

  if (value) return JSON.parse(value);
  return obj;
};